import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Tabs, Tab, TextField, MenuItem,
  Button, Alert, Table, TableHead, TableBody, TableRow, TableCell,
  IconButton, Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Paper, Chip, FormControlLabel, Switch, TablePagination, Autocomplete, Collapse,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { Home, School, People, TrendingUp, AttachMoney, Edit, Print, ContactPhone, EventNote, Analytics, EventAvailable, MenuBook, Search, ExpandMore, ExpandLess, CalendarMonth } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import TeacherAttendance from './TeacherAttendance';
import SubjectAnalysis from './SubjectAnalysis';
import AttendanceSnapshot from './AttendanceSnapshot';
import LearnerResultsDialog from './LearnerResultsDialog';
import ClassTimetableView from './ClassTimetableView';
import { fmtMoney } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Curriculum is inferred from the class name/code prefix (the school runs 8-4-4
// Forms and CBC Grades side by side): "Grade 7" / "G7" → CBC, "Form 3" / "F3" →
// 8-4-4. Anything else is left unclassified (shown under "All").
const curriculumOf = (label = '') => {
  const s = String(label).trim().toLowerCase();
  if (/^g(rade)?\b|^g\s?\d|grade/.test(s)) return 'CBC';
  if (/^f(orm)?\b|^f\s?\d|form/.test(s)) return '8-4-4';
  return 'Other';
};

export default function ClassTeacherCentre() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [classes, setClasses] = useState([]);
  const [subjectClasses, setSubjectClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [learners, setLearners] = useState([]);
  const [financeData, setFinanceData] = useState(null);
  const [performanceData, setPerformanceData] = useState([]);
  const [academicResults, setAcademicResults] = useState([]);
  const [guardians, setGuardians] = useState([]);
  const [curriculumSummary, setCurriculumSummary] = useState([]);
  const [events, setEvents] = useState([]);
  const [graduations, setGraduations] = useState([]);
  const [resultsLearner, setResultsLearner] = useState(null); // learner whose results drilldown is open
  const [expandedGrad, setExpandedGrad] = useState(null);     // batchRef of expanded graduation batch
  const [searchResults, setSearchResults] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [curriculum, setCurriculum] = useState(''); // '' = all, 'CBC', '8-4-4'

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [years, setYears] = useState([]);
  const [filterYear, setFilterYear] = useState('');
  const [filterTerm, setFilterTerm] = useState('');

  const [editingLearner, setEditingLearner] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const [gDialogOpen, setGDialogOpen] = useState(false);
  const [gForm, setGForm] = useState({ admNo: '', fullName: '', relationship: 'Guardian', phone: '', email: '', isPrimary: false });
  const editGuard = useUnsavedGuard({ open: editDialogOpen, value: editForm, onClose: () => setEditDialogOpen(false) });
  const gGuard    = useUnsavedGuard({ open: gDialogOpen,    value: gForm,    onClose: () => setGDialogOpen(false) });
  const [gSaving, setGSaving] = useState(false);

  // Client-side pagination for the long tables (data is already loaded per class)
  const [rpp, setRpp] = useState(25);
  const [pages, setPages] = useState({ learners: 0, results: 0, guardians: 0, finance: 0 });
  const setPage = (key, p) => setPages(s => ({ ...s, [key]: p }));
  const resetPages = () => setPages({ learners: 0, results: 0, guardians: 0, finance: 0 });
  const onRppChange = (e) => { setRpp(parseInt(e.target.value, 10)); resetPages(); };
  const pageSlice = (arr, key) => arr.slice(pages[key] * rpp, pages[key] * rpp + rpp);
  const Pager = ({ count, pageKey }) => (
    <TablePagination
      component="div" count={count} page={pages[pageKey]} rowsPerPage={rpp}
      onPageChange={(_, p) => setPage(pageKey, p)} onRowsPerPageChange={onRppChange}
      rowsPerPageOptions={[10, 25, 50, 100]}
      sx={{ '.MuiTablePagination-toolbar': { flexWrap: 'wrap' } }}
    />
  );

  const loadClasses = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/academics/class-teacher/classes`, { headers: hdrs() });
      setClasses(data);
      if (data.length > 0) setSelectedClass(data[0].classCode);
    } catch (err) {
      setError('Failed to load classes');
    }
  }, []);

  // Subject classes this teacher teaches (from TeacherAllocation) — the "My
  // Subject Classes" side of the unified centre, used by Attendance & Analysis.
  const loadSubjectClasses = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/academics/class-teacher/subject-classes`, { headers: hdrs() });
      setSubjectClasses(data);
    } catch (err) {}
  }, []);

  const loadYears = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/academics/years`, { headers: hdrs() });
      setYears(data);
      const active = data.find(y => y.isActive);
      if (active) setFilterYear(active.year);
    } catch (err) {}
  }, []);

  const loadLearners = useCallback(async () => {
    if (!selectedClass) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/academics/class-teacher/learners`, {
        headers: hdrs(),
        params: { classCode: selectedClass }
      });
      setLearners(data);
    } catch (err) {
      setError('Failed to load learners');
    } finally {
      setLoading(false);
    }
  }, [selectedClass]);

  const loadFinance = useCallback(async () => {
    if (!selectedClass) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/academics/class-teacher/finance`, {
        headers: hdrs(),
        params: { classCode: selectedClass, academicYear: filterYear, term: filterTerm }
      });
      setFinanceData(data);
    } catch (err) {
      setError('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  }, [selectedClass, filterYear, filterTerm]);

  const loadAcademicResults = useCallback(async () => {
    if (!selectedClass) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/academics/class-teacher/academic-results`, {
        headers: hdrs(),
        params: { classCode: selectedClass, academicYear: filterYear, term: filterTerm }
      });
      setAcademicResults(data);
    } catch (err) {
      setError('Failed to load academic results');
    } finally {
      setLoading(false);
    }
  }, [selectedClass, filterYear, filterTerm]);

  const loadPerformance = useCallback(async () => {
    if (!selectedClass) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/academics/class-teacher/performance`, {
        headers: hdrs(),
        params: { classCode: selectedClass, academicYear: filterYear, term: filterTerm }
      });
      setPerformanceData(data);
    } catch (err) {
      setError('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  }, [selectedClass, filterYear, filterTerm]);

  // Curriculum-progress across all the teacher's subject classes, for the
  // Overview "at a glance" coverage summary.
  const loadCurriculumSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/academics/class-teacher/curriculum-progress-summary`, { headers: hdrs() });
      setCurriculumSummary(data);
    } catch (err) { setCurriculumSummary([]); }
  }, []);

  // Upcoming school events (School Diary) for the Overview.
  const loadEvents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/academics/events`, { headers: hdrs() });
      setEvents((data.data || []).slice(0, 6));
    } catch (err) { setEvents([]); }
  }, []);

  // Graduation history (read-only) for the Graduated tab.
  const loadGraduations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/academics/class-teacher/graduations`, { headers: hdrs() });
      setGraduations(data);
    } catch (err) { setGraduations([]); }
  }, []);

  // Scoped quick learner search (header) — across the teacher's class-teacher classes.
  const searchLearners = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setSearchResults([]); return; }
    try {
      const { data } = await axios.get(`${API}/api/academics/class-teacher/search-learners`, {
        headers: hdrs(), params: { q },
      });
      setSearchResults(data);
    } catch (err) { setSearchResults([]); }
  }, []);

  const loadGuardians = useCallback(async () => {
    if (!selectedClass) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/academics/class-teacher/guardians`, {
        headers: hdrs(),
        params: { classCode: selectedClass }
      });
      setGuardians(data);
    } catch (err) {
      setError('Failed to load guardians');
    } finally {
      setLoading(false);
    }
  }, [selectedClass]);

  const openEditLearner = async (learner) => {
    try {
      const { data } = await axios.get(`${API}/api/academics/class-teacher/learner/${learner.admNo}`, { headers: hdrs() });
      setEditingLearner(data);
      setEditForm(data);
      setEditDialogOpen(true);
    } catch (err) {
      setError('Failed to load learner details');
    }
  };

  const handleSaveLearner = async () => {
    setEditSaving(true);
    try {
      await axios.put(`${API}/api/academics/class-teacher/learner/${editingLearner.admNo}`, editForm, { headers: hdrs() });
      setSuccess('Learner updated');
      setEditDialogOpen(false);
      loadLearners();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update learner');
    } finally {
      setEditSaving(false);
    }
  };

  const openAddGuardian = (admNo = '') => {
    setGForm({ admNo, fullName: '', relationship: 'Guardian', phone: '', email: '', isPrimary: false });
    setGDialogOpen(true);
  };

  const handleSaveGuardian = async () => {
    if (!gForm.admNo) { setError('Select a learner'); return; }
    if (!gForm.fullName.trim()) { setError('Guardian name is required'); return; }
    setGSaving(true);
    try {
      await axios.post(`${API}/api/academics/class-teacher/guardians`, gForm, { headers: hdrs() });
      setSuccess('Guardian added');
      setGDialogOpen(false);
      loadGuardians();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add guardian');
    } finally {
      setGSaving(false);
    }
  };

  // Load teacher's classes on mount
  useEffect(() => {
    loadClasses();
    loadYears();
    loadSubjectClasses();
  }, [loadClasses, loadYears, loadSubjectClasses]);

  // Curriculum-filtered views of the class / subject lists (8-4-4 vs CBC toggle).
  const filteredClasses = React.useMemo(
    () => curriculum ? classes.filter(c => curriculumOf(c.className || c.classCode) === curriculum) : classes,
    [classes, curriculum]
  );
  const filteredSubjectClasses = React.useMemo(
    () => curriculum ? subjectClasses.filter(s => curriculumOf(s.className || s.classCode || s.label) === curriculum) : subjectClasses,
    [subjectClasses, curriculum]
  );

  // If the current class falls outside the chosen curriculum, jump to the first
  // matching class (or clear).
  useEffect(() => {
    if (!curriculum) return;
    if (selectedClass && !filteredClasses.some(c => c.classCode === selectedClass)) {
      setSelectedClass(filteredClasses[0]?.classCode || '');
    }
  }, [curriculum, filteredClasses, selectedClass]);

  // Attendance scopes: the class(es) this teacher is class-teacher of (whole-class
  // attendance) + each subject class they teach (subject attendance).
  const attendanceTargets = React.useMemo(() => {
    const cls = filteredClasses.map(c => ({
      key: `class|${c.classCode}`, scope: 'class', classCode: c.classCode,
      areaCode: '', areaName: '', label: `${c.className} (whole class)`,
    }));
    const subs = filteredSubjectClasses
      .filter(s => s.classCode)
      .map(s => ({
        key: `subject|${s.classCode}|${s.areaCode}`, scope: 'subject',
        classCode: s.classCode, areaCode: s.areaCode, areaName: s.areaName,
        label: `${s.label} (subject)`,
      }));
    return [...cls, ...subs];
  }, [filteredClasses, filteredSubjectClasses]);

  // Group academic results by learner so the Results tab shows one row per
  // student (with subject count + mean), expandable to their per-subject marks —
  // instead of a long flat list repeating the learner on every subject.
  const groupedResults = React.useMemo(() => {
    const by = {};
    academicResults.forEach(r => {
      const key = r.learnerAdmNo || r.learnerName;
      const g = by[key] || (by[key] = { admNo: r.learnerAdmNo, name: r.learnerName, rows: [] });
      g.rows.push(r);
    });
    return Object.values(by).map(g => {
      const pcts = g.rows.map(r => r.percentage || 0);
      const mean = pcts.length ? pcts.reduce((a, b) => a + b, 0) / pcts.length : 0;
      return { ...g, subjects: g.rows.length, mean: +mean.toFixed(1) };
    }).sort((a, b) => b.mean - a.mean);
  }, [academicResults]);

  // Reset table pages whenever the scope changes so we never land on an empty page
  useEffect(() => { resetPages(); }, [selectedClass, filterYear, filterTerm]); // eslint-disable-line

  // Auto-refresh the active tab's data whenever the selected class, year, term
  // or tab changes — so a teacher switching between their classes immediately
  // sees the new class's data without clicking Refresh/View.
  useEffect(() => {
    if (!selectedClass) return;
    if (tab === 0) {
      // Overview tiles summarise every dataset — load them all
      loadLearners();
      loadFinance();
      loadAcademicResults();
      loadPerformance();
      loadCurriculumSummary();
      loadEvents();
    } else if (tab === 1) {
      loadLearners();
    } else if (tab === 2) {
      loadFinance();
    } else if (tab === 3) {
      loadAcademicResults();
    } else if (tab === 4) {
      loadPerformance();
    } else if (tab === 5) {
      loadGuardians();
    }
  }, [selectedClass, filterYear, filterTerm, tab, loadLearners, loadFinance, loadAcademicResults, loadPerformance, loadGuardians, loadCurriculumSummary, loadEvents]);

  // Graduated tab loads independently of a selected class.
  useEffect(() => { if (tab === 8) loadGraduations(); }, [tab, loadGraduations]);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: { xs: 1.5, sm: 3 }, mb: 4, px: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Card elevation={3} sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <School sx={{ color: '#1565c0', fontSize: { xs: 24, sm: 32 } }} />
              <Typography sx={{ color: '#1565c0', fontWeight: 700, typography: { xs: 'h6', sm: 'h5' } }}>
                Teacher Centre
              </Typography>
            </Box>
            <Box display="flex" gap={{ xs: 0.5, sm: 1 }} flexWrap="wrap" justifyContent="flex-end">
              <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => navigate('/academics/assessment-setup')} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                Setup Assessment
              </Button>
              <Button size="small" variant="outlined" startIcon={<TrendingUp />} onClick={() => navigate('/academics/score-entry')} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                Enter Marks
              </Button>
              <Button size="small" variant="outlined" startIcon={<Print />} onClick={() => navigate('/academics/assessment-report')} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                Reports
              </Button>
              <Button size="small" variant="outlined" startIcon={<MenuBook />} onClick={() => navigate('/staff/teaching')} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                My Teaching
              </Button>
              {/* Compact icon actions on small screens */}
              <Tooltip title="Setup Assessment"><IconButton size="small" onClick={() => navigate('/academics/assessment-setup')} sx={{ display: { xs: 'inline-flex', md: 'none' }, bgcolor: '#e1f5fe' }}><Edit fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Enter Marks"><IconButton size="small" onClick={() => navigate('/academics/score-entry')} sx={{ display: { xs: 'inline-flex', md: 'none' }, bgcolor: '#fce4ec' }}><TrendingUp fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Reports"><IconButton size="small" onClick={() => navigate('/academics/assessment-report')} sx={{ display: { xs: 'inline-flex', md: 'none' }, bgcolor: '#e0f2f1' }}><Print fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="My Teaching"><IconButton size="small" onClick={() => navigate('/staff/teaching')} sx={{ display: { xs: 'inline-flex', md: 'none' }, bgcolor: '#e8f5e9' }}><MenuBook fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Dashboard">
                <IconButton size="small" onClick={() => navigate('/dashboard')} sx={{ bgcolor: 'background.paper' }}>
                  <Home fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

          <Box display="flex" justifyContent="flex-end" mb={1}>
            <ToggleButtonGroup size="small" exclusive value={curriculum}
              onChange={(_, v) => setCurriculum(v ?? '')}>
              <ToggleButton value="" sx={{ py: 0.3, px: 1.2 }}>All</ToggleButton>
              <ToggleButton value="CBC" sx={{ py: 0.3, px: 1.2 }}>CBC</ToggleButton>
              <ToggleButton value="8-4-4" sx={{ py: 0.3, px: 1.2 }}>8-4-4</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box display="flex" gap={{ xs: 1, sm: 2 }} flexWrap="wrap" alignItems="center">
            <TextField
              select
              label="Select Class"
              value={selectedClass}
              onChange={e => setSelectedClass(e.target.value)}
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 200 } }}
            >
              {filteredClasses.length === 0 && <MenuItem value="" disabled>No classes</MenuItem>}
              {filteredClasses.map(c => (
                <MenuItem key={c.classCode} value={c.classCode}>
                  {c.className}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Academic Year"
              value={filterYear}
              onChange={e => setFilterYear(e.target.value)}
              size="small"
              sx={{ minWidth: { xs: 'calc(60% - 4px)', sm: 140 }, flexGrow: { xs: 1, sm: 0 } }}
            >
              {years.map(y => (
                <MenuItem key={y.year} value={y.year}>
                  {y.year}{y.isActive ? ' (Active)' : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Term"
              value={filterTerm}
              onChange={e => setFilterTerm(e.target.value)}
              size="small"
              sx={{ minWidth: { xs: 'calc(40% - 4px)', sm: 110 } }}
            >
              <MenuItem value="">All Terms</MenuItem>
              {[1, 2, 3].map(t => (
                <MenuItem key={t} value={String(t)}>
                  Term {t}
                </MenuItem>
              ))}
            </TextField>
            <Autocomplete
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 260 }, flexGrow: { sm: 1 } }}
              options={searchResults}
              filterOptions={(x) => x}
              getOptionLabel={o => (o && o.name) ? `${o.name} — ${o.admNo}` : ''}
              isOptionEqualToValue={(o, v) => o.admNo === v.admNo}
              inputValue={searchInput}
              onInputChange={(_, v) => { setSearchInput(v); searchLearners(v); }}
              onChange={(_, val) => {
                if (val) { setSelectedClass(val.classCode); setTab(1); setSearchInput(''); setSearchResults([]); }
              }}
              noOptionsText={searchInput.length < 2 ? 'Type a name or adm no…' : 'No learners found'}
              renderOption={(props, o) => (
                <li {...props} key={o.admNo}>
                  <Box>
                    <Typography variant="body2">{o.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{o.admNo} · {o.className}</Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Search student" placeholder="Name or adm no"
                  InputProps={{ ...params.InputProps, startAdornment: <Search fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} /> }} />
              )}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card elevation={2}>
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, sm: 3 },
              '& .MuiTab-root': { minHeight: 48, fontSize: { xs: 12, sm: 14 }, textTransform: 'none' } }}>
            <Tab label="Overview" icon={<School />} iconPosition="start" />
            <Tab label="Learners" icon={<People />} iconPosition="start" />
            <Tab label="Finance" icon={<AttachMoney />} iconPosition="start" />
            <Tab label="Results" icon={<TrendingUp />} iconPosition="start" />
            <Tab label="Performance" icon={<TrendingUp />} iconPosition="start" />
            <Tab label="Guardians" icon={<ContactPhone />} iconPosition="start" />
            <Tab label="Attendance" icon={<EventAvailable />} iconPosition="start" />
            <Tab label="Subject Analysis" icon={<Analytics />} iconPosition="start" />
            <Tab label="Graduated" icon={<School />} iconPosition="start" />
            <Tab label="Timetable" icon={<CalendarMonth />} iconPosition="start" />
          </Tabs>

          {/* Tab 0: Overview — menu of class-teacher features */}
          {tab === 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {classes.find(c => c.classCode === selectedClass)?.className || 'My Class'} — Menu
              </Typography>
              {!selectedClass && subjectClasses.length === 0 ? (
                <Typography color="text.secondary">You are not assigned to any class or subject yet. Contact your administrator.</Typography>
              ) : (
                <>
                {!selectedClass && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    You are not a class teacher of any class, but you teach {subjectClasses.length} subject class(es).
                    Use the <strong>Attendance</strong> and <strong>Subject Analysis</strong> tabs.
                  </Alert>
                )}

                <AttendanceSnapshot
                  selectedClass={selectedClass}
                  className={classes.find(c => c.classCode === selectedClass)?.className}
                  subjectClasses={filteredSubjectClasses}
                  year={filterYear}
                  term={filterTerm}
                  onOpenAttendance={() => setTab(6)}
                />

                {curriculumSummary.filter(r => !curriculum || curriculumOf(r.className) === curriculum).length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        <MenuBook sx={{ verticalAlign: 'middle', mr: 0.5 }} fontSize="small" />
                        Curriculum Progress — my subjects
                      </Typography>
                      <Button size="small" onClick={() => setTab(7)}>Details</Button>
                    </Box>
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ minWidth: 560 }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell><strong>Subject Class</strong></TableCell>
                            <TableCell align="center"><strong>Formative</strong></TableCell>
                            <TableCell align="center"><strong>Projects</strong></TableCell>
                            <TableCell align="center"><strong>Summative</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {curriculumSummary.filter(r => !curriculum || curriculumOf(r.className) === curriculum).map(r => {
                            const cell = (b) => b.total === 0
                              ? <Typography variant="caption" color="text.disabled">—</Typography>
                              : <Chip size="small" label={`${b.analyzed}/${b.total} · ${b.pct}%`}
                                  sx={{ bgcolor: b.pct >= 100 ? '#e8f5e9' : b.pct > 0 ? '#fff8e1' : '#ffebee' }} />;
                            return (
                              <TableRow key={`${r.classCode}|${r.areaCode}`} hover>
                                <TableCell>{r.className} — {r.areaName}{!r.hasSetup && <Chip size="small" variant="outlined" label="no plan" sx={{ ml: 1 }} />}</TableCell>
                                <TableCell align="center">{cell(r.formative)}</TableCell>
                                <TableCell align="center">{cell(r.projects)}</TableCell>
                                <TableCell align="center">{cell(r.summative)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  </Paper>
                )}

                {events.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        <EventNote sx={{ verticalAlign: 'middle', mr: 0.5 }} fontSize="small" />
                        School Diary — upcoming
                      </Typography>
                      <Button size="small" onClick={() => navigate('/academics/events')}>All events</Button>
                    </Box>
                    {events.map(ev => (
                      <Box key={ev._id} display="flex" alignItems="center" gap={1.5} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ textAlign: 'center', minWidth: 52 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1 }}>
                            {new Date(ev.date).toLocaleDateString('en-GB', { day: '2-digit' })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(ev.date).toLocaleDateString('en-GB', { month: 'short' })}
                          </Typography>
                        </Box>
                        <Box flexGrow={1}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{ev.title}</Typography>
                          {ev.venue && <Typography variant="caption" color="text.secondary">{ev.venue}</Typography>}
                        </Box>
                        <Chip size="small" label={ev.category} variant="outlined" />
                      </Box>
                    ))}
                  </Paper>
                )}

                <Grid container spacing={2}>
                  {[
                    { title: 'Learners',        sub: `${learners.length} in class`,        color: '#e3f2fd', Icon: People,       go: () => setTab(1) },
                    { title: 'Graduated',       sub: 'Past classes',                       color: '#eceff1', Icon: School,       go: () => setTab(8) },
                    { title: 'Attendance',      sub: 'Mark class / subject',               color: '#e8eaf6', Icon: EventAvailable, go: () => setTab(6) },
                    { title: 'Timetable',       sub: 'Class schedule (read-only)',         color: '#e0f7fa', Icon: CalendarMonth, go: () => setTab(9) },
                    { title: 'Subject Analysis', sub: `${filteredSubjectClasses.length} subject class(es)`, color: '#e0f7fa', Icon: MenuBook,   go: () => setTab(7) },
                    { title: 'Exam Results',    sub: `${academicResults.length} records`,   color: '#e8f5e9', Icon: TrendingUp,   go: () => setTab(3) },
                    { title: 'Class Performance', sub: `${performanceData.length} subjects`, color: '#ede7f6', Icon: TrendingUp,   go: () => setTab(4) },
                    { title: 'LearnAlytics',    sub: 'Trends, projections, risks',          color: '#e0f2f1', Icon: Analytics,    go: () => navigate('/academics/lernalytics', { state: { mode: 'class', classCode: selectedClass } }) },
                    { title: 'Fee Balances',    sub: financeData?.totalBalance != null ? fmtMoney(financeData.totalBalance) : 'View', color: '#f3e5f5', Icon: AttachMoney, go: () => setTab(2) },
                    { title: 'Guardians',       sub: 'Parent contacts',                     color: '#fff8e1', Icon: ContactPhone, go: () => setTab(5) },
                    { title: 'School Events',   sub: 'Calendar & activities',               color: '#f3e5f5', Icon: EventNote,    go: () => navigate('/academics/events') },
                    { title: 'Setup Assessment', sub: 'Rubrics & components',               color: '#e1f5fe', Icon: Edit,         go: () => navigate('/academics/assessment-setup') },
                    { title: 'Enter Marks',     sub: 'Upload exam scores',                  color: '#fce4ec', Icon: Edit,         go: () => navigate('/academics/score-entry') },
                    { title: 'Reports',         sub: 'Report cards & summaries',            color: '#e0f2f1', Icon: Print,        go: () => navigate('/academics/assessment-report') },
                    { title: 'Merit List',      sub: 'Class ranking',                       color: '#fff3e0', Icon: TrendingUp,   go: () => navigate('/academics/merit-list') },
                  ].map(card => (
                    <Grid item xs={6} sm={4} md={3} key={card.title}>
                      <Paper
                        onClick={card.go}
                        sx={{ p: 2, textAlign: 'center', bgcolor: card.color, cursor: 'pointer', transition: 'transform .15s, box-shadow .15s',
                          '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 } }}
                      >
                        <card.Icon sx={{ fontSize: 34, color: '#1565c0', mb: 0.5 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{card.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{card.sub}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                </>
              )}
            </Box>
          )}

          {/* Tab 1: Learners */}
          {tab === 1 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Learners in {classes.find(c => c.classCode === selectedClass)?.className}</Typography>
                <Button size="small" onClick={loadLearners} disabled={loading}>Refresh</Button>
              </Box>
              {loading ? (
                <CircularProgress />
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 560 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Adm No</strong></TableCell>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Gender</strong></TableCell>
                      <TableCell><strong>DOB</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageSlice(learners, 'learners').map(l => (
                      <TableRow key={l.admNo} hover>
                        <TableCell>{l.admNo}</TableCell>
                        <TableCell>{l.name}</TableCell>
                        <TableCell>{l.gender}</TableCell>
                        <TableCell>{l.dateOfBirth ? new Date(l.dateOfBirth).toLocaleDateString() : '—'}</TableCell>
                        <TableCell>{l.studentType}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit Biodata">
                            <IconButton size="small" onClick={() => openEditLearner(l)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </Box>
              )}
              {learners.length > 10 && <Pager count={learners.length} pageKey="learners" />}
            </Box>
          )}

          {/* Tab 2: Finance */}
          {tab === 2 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Class Fee Statement</Typography>
                <Button size="small" onClick={loadFinance} disabled={loading} startIcon={<Print />}>Print</Button>
              </Box>
              {loading ? (
                <CircularProgress />
              ) : financeData ? (
                <Box>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                        <Typography variant="caption" color="text.secondary">Learners</Typography>
                        <Typography variant="h6">{financeData.learnerCount}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
                        <Typography variant="caption" color="text.secondary">Total Invoiced</Typography>
                        <Typography variant="h6">{fmtMoney(financeData.totalInvoiced)}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 2, bgcolor: '#e8f5e9' }}>
                        <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                        <Typography variant="h6">{fmtMoney(financeData.totalPaid)}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 2, bgcolor: financeData.totalBalance > 0 ? '#ffebee' : '#e8f5e9' }}>
                        <Typography variant="caption" color="text.secondary">Total Balance</Typography>
                        <Typography variant="h6" sx={{ color: financeData.totalBalance > 0 ? '#d32f2f' : '#2e7d32' }}>
                          {fmtMoney(financeData.totalBalance)}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                  <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 520 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell><strong>Adm No</strong></TableCell>
                        <TableCell><strong>Name</strong></TableCell>
                        <TableCell align="right"><strong>Invoiced</strong></TableCell>
                        <TableCell align="right"><strong>Paid</strong></TableCell>
                        <TableCell align="right"><strong>Balance</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageSlice(financeData.learners, 'finance').map(l => (
                        <TableRow key={l.admNo}>
                          <TableCell>{l.admNo}</TableCell>
                          <TableCell>{l.name}</TableCell>
                          <TableCell align="right">{fmtMoney(l.invoiced)}</TableCell>
                          <TableCell align="right">{fmtMoney(l.paid)}</TableCell>
                          <TableCell align="right" sx={{ color: l.balance > 0 ? '#d32f2f' : '#2e7d32' }}>
                            <strong>{fmtMoney(l.balance)}</strong>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </Box>
                  {financeData.learners.length > 10 && <Pager count={financeData.learners.length} pageKey="finance" />}
                </Box>
              ) : (
                <Typography>No finance data available</Typography>
              )}
            </Box>
          )}

          {/* Tab 3: Academic Results — grouped per student, click to expand */}
          {tab === 3 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Academic Results</Typography>
                <Button size="small" onClick={loadAcademicResults} disabled={loading}>Refresh</Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                One row per learner — click a learner to drill into their results with year, term, assessment and learning-area filters.
              </Typography>
              {loading ? (
                <CircularProgress />
              ) : groupedResults.length === 0 ? (
                <Typography color="text.secondary">No results entered for this class yet.</Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 480 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Learner</strong></TableCell>
                      <TableCell align="center"><strong>Records</strong></TableCell>
                      <TableCell align="center"><strong>Mean %</strong></TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageSlice(groupedResults, 'results').map(g => (
                      <TableRow key={g.admNo || g.name} hover sx={{ cursor: 'pointer' }}
                        onClick={() => setResultsLearner({ admNo: g.admNo, name: g.name })}>
                        <TableCell><strong>{g.name}</strong>{g.admNo && <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{g.admNo}</Typography>}</TableCell>
                        <TableCell align="center">{g.subjects}</TableCell>
                        <TableCell align="center"><strong>{g.mean}%</strong></TableCell>
                        <TableCell align="right">
                          <Button size="small" endIcon={<TrendingUp />}>View results</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </Box>
              )}
              {groupedResults.length > 10 && <Pager count={groupedResults.length} pageKey="results" />}
            </Box>
          )}

          {/* Tab 4: Class Performance */}
          {tab === 4 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Class Performance Summary</Typography>
                <Button size="small" onClick={loadPerformance} disabled={loading}>Refresh</Button>
              </Box>
              {loading ? (
                <CircularProgress />
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 480 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Subject</strong></TableCell>
                      <TableCell align="center"><strong>Students</strong></TableCell>
                      <TableCell align="center"><strong>Average</strong></TableCell>
                      <TableCell align="center"><strong>Highest</strong></TableCell>
                      <TableCell align="center"><strong>Lowest</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {performanceData.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell>{p.subjectName}</TableCell>
                        <TableCell align="center">{p.count}</TableCell>
                        <TableCell align="center"><strong>{p.average}%</strong></TableCell>
                        <TableCell align="center">{p.highest}%</TableCell>
                        <TableCell align="center">{p.lowest}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </Box>
              )}
            </Box>
          )}

          {/* Tab 5: Guardians */}
          {tab === 5 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1} flexWrap="wrap">
                <Typography variant="h6">Guardians / Parents</Typography>
                <Box display="flex" gap={1}>
                  <Button size="small" variant="contained" startIcon={<ContactPhone />} onClick={() => openAddGuardian()}>Add Guardian</Button>
                  <Button size="small" onClick={loadGuardians} disabled={loading}>Refresh</Button>
                </Box>
              </Box>
              {loading ? (
                <CircularProgress />
              ) : guardians.length === 0 ? (
                <Typography color="text.secondary">No guardians recorded for learners in this class.</Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 640 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Learner</strong></TableCell>
                      <TableCell><strong>Guardian</strong></TableCell>
                      <TableCell><strong>Relationship</strong></TableCell>
                      <TableCell><strong>Phone</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell align="center"><strong>Primary</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageSlice(guardians, 'guardians').map((g, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{g.learnerName}</TableCell>
                        <TableCell>{g.guardianName}</TableCell>
                        <TableCell>{g.relationship}</TableCell>
                        <TableCell>{g.phone ? <a href={`tel:${g.phone}`}>{g.phone}</a> : '—'}</TableCell>
                        <TableCell>{g.email ? <a href={`mailto:${g.email}`}>{g.email}</a> : '—'}</TableCell>
                        <TableCell align="center">
                          {g.isPrimary ? <Chip label="Primary" size="small" color="primary" /> : (g.isEmergency ? <Chip label="Emergency" size="small" color="warning" /> : '—')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </Box>
              )}
              {guardians.length > 10 && <Pager count={guardians.length} pageKey="guardians" />}
            </Box>
          )}

          {/* Tab 6: Attendance (class or subject) */}
          {tab === 6 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Attendance</Typography>
              <TeacherAttendance targets={attendanceTargets} year={filterYear} term={filterTerm} />
            </Box>
          )}

          {/* Tab 7: Subject Analysis (performance over time) */}
          {tab === 7 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Subject Analysis</Typography>
              <SubjectAnalysis subjectClasses={filteredSubjectClasses} year={filterYear} term={filterTerm} />
            </Box>
          )}

          {/* Tab 8: Graduated classes (read-only history) */}
          {tab === 8 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Graduated Classes</Typography>
                <Button size="small" onClick={loadGraduations} disabled={loading}>Refresh</Button>
              </Box>
              {graduations.length === 0 ? (
                <Typography color="text.secondary">No graduation records yet.</Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 520 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell width={40} />
                      <TableCell><strong>Graduation Year</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell align="center"><strong>Graduates</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {graduations.map(b => {
                      const open = expandedGrad === b.batchRef;
                      return (
                        <React.Fragment key={b.batchRef}>
                          <TableRow hover sx={{ cursor: 'pointer' }}
                            onClick={() => setExpandedGrad(open ? null : b.batchRef)}>
                            <TableCell><IconButton size="small">{open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}</IconButton></TableCell>
                            <TableCell><strong>{b.academicYear}</strong></TableCell>
                            <TableCell>{b.promotionDate ? new Date(b.promotionDate).toLocaleDateString() : '—'}</TableCell>
                            <TableCell align="center"><Chip size="small" label={b.count} /></TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={4} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                              <Collapse in={open} timeout="auto" unmountOnExit>
                                <Box sx={{ my: 1, ml: 2 }}>
                                  <Table size="small">
                                    <TableHead><TableRow><TableCell><strong>Adm No</strong></TableCell><TableCell><strong>Name</strong></TableCell></TableRow></TableHead>
                                    <TableBody>
                                      {b.learners.map(l => (
                                        <TableRow key={l.admNo}><TableCell>{l.admNo}</TableCell><TableCell>{l.name}</TableCell></TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
                </Box>
              )}
            </Box>
          )}

          {/* Tab 9: Timetable (read-only, with class filter) */}
          {tab === 9 && (
            <ClassTimetableView initialClassCode={selectedClass} />
          )}
        </CardContent>
      </Card>

      {/* Learner Results drilldown */}
      <LearnerResultsDialog
        open={!!resultsLearner}
        learner={resultsLearner}
        onClose={() => setResultsLearner(null)}
      />

      {/* Edit Learner Dialog */}
      {editGuard.discardDialog}
      <Dialog open={editDialogOpen} onClose={editGuard.attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Learner Biodata</DialogTitle>
        <DialogContent dividers>
          {editingLearner && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mt: 2 }}>
              <TextField
                label="Full Name"
                value={editForm.name || ''}
                onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                fullWidth
              />
              <TextField
                select
                label="Gender"
                value={editForm.gender || ''}
                onChange={e => setEditForm(f => ({ ...f, gender: e.target.value }))}
                fullWidth
              >
                <MenuItem value="">— Not set —</MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
              <TextField
                label="Date of Birth"
                type="date"
                value={editForm.dateOfBirth ? editForm.dateOfBirth.split('T')[0] : ''}
                onChange={e => setEditForm(f => ({ ...f, dateOfBirth: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Place of Birth"
                value={editForm.placeOfBirth || ''}
                onChange={e => setEditForm(f => ({ ...f, placeOfBirth: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Religion"
                value={editForm.religion || ''}
                onChange={e => setEditForm(f => ({ ...f, religion: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Nationality"
                value={editForm.nationality || ''}
                onChange={e => setEditForm(f => ({ ...f, nationality: e.target.value }))}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={editGuard.attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveLearner} disabled={editSaving}>
            {editSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Guardian Dialog */}
      {gGuard.discardDialog}
      <Dialog open={gDialogOpen} onClose={gGuard.attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Guardian</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2, mt: 1 }}>
            <TextField select label="Learner" value={gForm.admNo}
              onChange={e => setGForm(f => ({ ...f, admNo: e.target.value }))} fullWidth>
              {learners.map(l => <MenuItem key={l.admNo} value={l.admNo}>{l.admNo} — {l.name}</MenuItem>)}
            </TextField>
            <TextField label="Guardian Full Name" value={gForm.fullName}
              onChange={e => setGForm(f => ({ ...f, fullName: e.target.value }))} fullWidth />
            <TextField select label="Relationship" value={gForm.relationship}
              onChange={e => setGForm(f => ({ ...f, relationship: e.target.value }))} fullWidth>
              {['Father', 'Mother', 'Guardian', 'Grandfather', 'Grandmother', 'Aunt', 'Uncle', 'Sibling', 'Other']
                .map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            <TextField label="Phone" value={gForm.phone}
              onChange={e => setGForm(f => ({ ...f, phone: e.target.value }))} fullWidth />
            <TextField label="Email" value={gForm.email}
              onChange={e => setGForm(f => ({ ...f, email: e.target.value }))} fullWidth />
          </Box>
          <FormControlLabel sx={{ mt: 1 }}
            control={<Switch checked={gForm.isPrimary} onChange={e => setGForm(f => ({ ...f, isPrimary: e.target.checked }))} />}
            label="Primary contact (main fee & notice contact)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={gGuard.attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveGuardian} disabled={gSaving}>
            {gSaving ? 'Saving...' : 'Add Guardian'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
